import { Component, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import { Order } from '../../model/order';
import { AdminService, adminFailure } from '../../service/admin.service';
import { SeoService } from '../../service/seo.service';
import { MoneyPipe } from '../../money.pipe';
import { orderStatusLabel } from './status-labels';

const ACTIONS: Record<string, string> = {
  APPROVED: 'Підтвердити',
  SENT_TO_CLIENT: 'Відправлено',
  DONE: 'Завершити',
  CANCELED: 'Скасувати'
};

/** The `order-close` page: one order, and the two ways it can end. */
@Component({
  selector: 'admin-order-page',
  imports: [RouterLink, MoneyPipe],
  host: {
    style: 'display: contents'
  },
  template: `
    <section class="bg-title-page p-t-15 p-b-16 flex-col-c-m">
      <h1 class="l-text2 t-center">
        Замовлення №{{code()}}
      </h1>
    </section>

    <section class="cart bgwhite p-t-70 p-b-100">
      <div class="container">
        @if (failure(); as failure) {
          <p class="s-text5 text-danger text-center p-b-20">{{failure}}</p>
        }
        @if (order(); as order) {
          <div class="row">
            <div class="col-lg bo9 p-l-40 p-r-40 p-t-30 p-b-8 m-t-30 m-r-3 m-l-3 p-lr-15-sm">
              <h5 class="m-text20 p-b-24">Товари</h5>
              @for (product of order.products; track $index) {
                <div class="flex-w flex-sb-m p-b-12">
                  <span class="s-text18 w-size19 w-full-sm flex-m" style="gap: 10px">
                    @if (product.uri) {
                      <a class="admin-order-product-photo" [routerLink]="product.uri">
                        @if (product.photo) {
                          <img style="width: 60px; height: 60px; object-fit: cover" [src]="product.photo"
                            [alt]="product.name">
                        }
                      </a>
                      <a class="s-text18" [routerLink]="product.uri">{{product.name}}</a>
                    } @else {
                      {{product.name}}
                    }
                  </span>
                  <span class="m-text21 w-size20 w-full-sm">
                    <small>{{product.price | money}} x {{product.quantity}}</small>
                  </span>
                </div>
              }
              <div class="flex-w flex-sb-m p-t-15 p-b-12">
                <span class="m-text22 w-size19 w-full-sm">Загалом:</span>
                <span class="m-text21 w-size20 w-full-sm">
                  {{order.total | money}}
                </span>
              </div>
            </div>

            @if (order.shipping; as shipping) {
              <div class="col-lg bo9 p-l-40 p-r-40 p-t-30 p-b-8 m-t-30 m-r-3 m-l-3 p-lr-15-sm">
                <h5 class="m-text20 p-b-24">Дані доставки</h5>
                <div class="flex-w flex-sb p-t-15 p-b-20">
                  <span class="s-text18 w-size19 w-full-sm">Служба доставки:</span>
                  <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.shippingCompany}}</p></div>

                  <span class="s-text18 w-size19 w-full-sm">Одержувач:</span>
                  <div class="w-size20 w-full-sm">
                    <p class="s-text8 p-b-23">{{shipping.address.firstName}} {{shipping.address.lastName}}</p>
                  </div>

                  <span class="s-text18 w-size19 w-full-sm">Телефон:</span>
                  <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.address.phone}}</p></div>

                  @if (shipping.address.city) {
                    <span class="s-text18 w-size19 w-full-sm">Місто:</span>
                    <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.address.city}}</p></div>
                  }

                  @if (shipping.address.street) {
                    <span class="s-text18 w-size19 w-full-sm">Адреса:</span>
                    <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.address.street}}</p></div>
                  }

                  @if (shipping.paymentOption) {
                    <span class="s-text18 w-size19 w-full-sm">Оплата:</span>
                    <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.paymentOption}}</p></div>
                  }

                  @if (order.paymentReference) {
                    <span class="s-text18 w-size19 w-full-sm">Номер платежу:</span>
                    <div class="w-size20 w-full-sm">
                      <p class="s-text8 p-b-23">{{order.paymentReference}}</p>
                    </div>
                  }

                  @if (shipping.comment) {
                    <span class="s-text18 w-size19 w-full-sm">Коментар:</span>
                    <div class="w-size20 w-full-sm"><p class="s-text8 p-b-23">{{shipping.comment}}</p></div>
                  }
                </div>
              </div>
            }
          </div>

          <div class="flex-w p-t-30" style="gap: 10px">
            <span class="s-text8">Статус: {{label(order.status)}}</span>
            <!-- Only the next steps: the stock count is kept for no other path. -->
            @for (status of order.nextStatuses ?? []; track status) {
              <button type="button" class="size9 bg1 bo-rad-23 hov1 s-text1 trans-0-4 admin-order-status"
                [disabled]="changing()" (click)="changeStatus(status)">
                {{action(order.status, status)}}
              </button>
            }
            @if (changing()) {
              <span class="s-text8">Збереження…</span>
            }
            <a routerLink="/orders" class="s-text8">&lt; До списку замовлень</a>
          </div>
        } @else if (!failure()) {
          <p class="text-center">Завантаження…</p>
        }
      </div>
    </section>
    `
})
export class AdminOrderPageComponent {

  /** Bound from the route's `:code` parameter by `withComponentInputBinding`. */
  readonly code = input.required<string>();

  private service = inject(AdminService);

  readonly order = signal<Order | undefined>(undefined);
  readonly failure = signal<string | undefined>(undefined);
  /** A status step moves stock, so a second press must wait for the first to answer. */
  readonly changing = signal(false);
  readonly label = orderStatusLabel;

  constructor() {
    const seo = inject(SeoService);
    seo.apply({ title: 'Замовлення', robots: 'noindex,nofollow' }, true);
    seo.openGraph(undefined);
    effect(() => {
      const code = this.code();
      this.order.set(undefined);
      this.failure.set(undefined);
      void this.service.order(code).then(
        order => this.order.set(order),
        error => this.failure.set(adminFailure(error, `Не вдалося знайти замовлення №${code}.`)));
    });
  }

  action(from: string, to: string) {
    // A parcel already sent is not cancelled but returned, and its stock goes back on the shelf
    return from === 'SENT_TO_CLIENT' && to === 'CANCELED' ? 'Повернуто' : ACTIONS[to] ?? to;
  }

  async changeStatus(status: string) {
    this.failure.set(undefined);
    this.changing.set(true);
    try {
      this.order.set(await this.service.changeOrderStatus(this.code(), status));
    } catch (error) {
      this.failure.set(adminFailure(error, 'Не вдалося змінити статус. Спробуйте ще раз.'));
      if ((error as { status?: number } | null)?.status === 400) {
        // Refused because the order has moved on elsewhere, so show where it is now
        this.order.set(await this.service.order(this.code()).catch(() => this.order()));
      }
    } finally {
      this.changing.set(false);
    }
  }

}
